// walkthrough/predicates/device-scope.js — K849: completion contracts for the
// device-scoping journey.
//
// Spec section 3: "A click can produce a validation failure, a permission
// denial, a network error, a stale-record conflict, a confirmation dialog, or
// an accepted request whose execution is still pending. None of those is
// completion." So the consequential step here does not watch the button. It
// reads the rule back from the server through the same RPC the page uses.
//
// listScopeRules is INJECTED rather than imported, for two reasons: it keeps
// this file free of a dependency cycle with endpointApi.js, and it makes every
// predicate testable without a Supabase client.

/* The baseline problem, which is the whole reason this file is longer than
   four one-liners.

   The naive check is "after the save, does a rule matching what they typed
   exist?" That reports success when the rule ALREADY existed and the save
   failed - which is the most likely failure here, because a tenant retrying a
   rule they added last week is exactly the case that errors. Existence is not
   evidence of this save.

   So: snapshot the rule ids before the save can happen, and require a rule
   that is BOTH new and matches what the user typed.

   The residual hole, stated rather than papered over: if a second admin adds
   the identical rule in the same few seconds while this user's save fails,
   the new id matches and this returns ok. Narrowing that further would need a
   server-side echo of the created id, which device_scope_rule_set does return
   - wiring that is the better fix and is noted as the next step rather than
   claimed here. */

const SEL = {
  panel:   '[data-testid="ep-scope"]',
  kind:    '[data-testid="ep-scope-kind"]',
  value:   '[data-testid="ep-scope-value"]',
  preview: '[data-testid="ep-scope-preview"]',
  submit:  '[data-testid="ep-scope-submit"]',
};

function visible(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return false;
  const cs = window.getComputedStyle(el);
  return cs.visibility !== 'hidden' && cs.display !== 'none';
}

function one(sel) {
  // Ambiguity is a stop, not a pick. Same rule as the resolver: taking the
  // first match is how a guide points at the wrong row.
  const all = document.querySelectorAll(sel);
  if (all.length === 1) return { el: all[0], ambiguous: false, n: 1 };
  return { el: null, ambiguous: all.length > 1, n: all.length };
}

export function makeDeviceScopePredicates({ tenantId, listScopeRules }) {
  if (typeof listScopeRules !== 'function') {
    throw new Error('makeDeviceScopePredicates: listScopeRules must be injected');
  }

  let baseline = null;      // Set<string> of rule ids, or null when unarmed
  let armedFor = null;      // { kind, value } the user had typed when armed
  let armError = null;

  async function readRules() {
    const res = await listScopeRules(tenantId);
    if (res && res.error) {
      const e = res.error;
      throw new Error((e && (e.message || e.hint)) || 'the rules could not be read');
    }
    return (res && res.data) || [];
  }

  const api = {
    /* The host calls this when the SAVE step becomes ready - after the value
       is entered and before the user can click. If it is never called,
       scope_rule_saved refuses rather than falling back to an existence check:
       a weaker check that still says "saved" is worse than an honest stop. */
    async armSave() {
      armError = null;
      try {
        const rules = await readRules();
        baseline = new Set(rules.map((r) => String(r.id)));
        const k = one(SEL.kind), v = one(SEL.value);
        armedFor = {
          kind:  k.el ? String(k.el.value || '') : null,
          value: v.el ? String(v.el.value || '').trim() : null,
        };
        return { ok: true, baseline_size: baseline.size, armed_for: armedFor };
      } catch (err) {
        baseline = null;
        armError = (err && err.message) || 'the rules could not be read';
        return { ok: false, reason: armError };
      }
    },

    reset() { baseline = null; armedFor = null; armError = null; },

    // ---- mechanical steps -------------------------------------------------
    async device_scope_visible() {
      const { el, ambiguous, n } = one(SEL.panel);
      if (ambiguous) return { ok: false, reason: 'the Device scope panel matched ' + n + ' elements, so I stopped rather than point at the wrong one' };
      if (!visible(el)) return { ok: false, reason: 'the Device scope panel is not on screen yet' };
      return { ok: true };
    },

    async method_chooser_visible() {
      const { el, ambiguous, n } = one(SEL.kind);
      if (ambiguous) return { ok: false, reason: 'the method chooser matched ' + n + ' elements' };
      if (!visible(el)) {
        /* The form only renders for owner or admin - the component shows
           "Changing device scope needs owner or admin rights" instead. Saying
           that is more useful than "not found". */
        return { ok: false, reason: 'the method chooser is not on screen. Device scope can only be changed by an owner or an admin, and the form is hidden otherwise' };
      }
      return { ok: true };
    },

    async scope_value_entered() {
      const { el, ambiguous } = one(SEL.value);
      if (ambiguous || !el) return { ok: false, reason: 'I cannot find the value field' };
      const v = String(el.value || '').trim();
      if (!v) return { ok: false, reason: 'nothing has been typed yet' };
      return { ok: true };
    },

    async preview_rendered() {
      const { el, ambiguous } = one(SEL.preview);
      if (ambiguous) return { ok: false, reason: 'the preview matched more than one element' };
      if (!visible(el)) {
        /* The preview is conditionally rendered on a non-empty value, and for
           a name pattern only when scopeGlob() returns a usable regex. So its
           absence is a real signal, not a timing problem. */
        return { ok: false, reason: 'the preview has not appeared. It shows once the value is usable - for a name pattern, a value of only wildcards is refused, so nothing is previewed' };
      }
      return { ok: true };
    },

    // ---- the consequential step -------------------------------------------
    async scope_rule_saved() {
      if (baseline === null) {
        return { ok: false, reason: armError
          ? 'I could not read the existing rules before the save (' + armError + '), so I cannot tell a new rule from one that was already there. I will not call this saved on a weaker check.'
          : 'I did not record the rules before the save, so I cannot prove this one is new rather than pre-existing. Stopping instead of guessing.' };
      }

      let rules;
      try { rules = await readRules(); }
      catch (err) { return { ok: false, reason: 'I could not read the rules back (' + ((err && err.message) || 'unknown') + '), so I cannot confirm the save' }; }

      const wanted = armedFor || {};
      const sameRule = (r) => {
        if (!wanted.value) return false;
        const rv = String(r.value ?? r.rule_value ?? '').trim().toLowerCase();
        const rk = String(r.kind ?? r.rule_kind ?? '').trim();
        return rv === String(wanted.value).toLowerCase()
            && (!wanted.kind || rk === wanted.kind);
      };

      const fresh = rules.filter((r) => !baseline.has(String(r.id)));
      const match = fresh.find(sameRule);

      if (match) return { ok: true, rule_id: String(match.id) };

      if (fresh.length) {
        /* A rule appeared, but not the one they typed. Reporting "saved" here
           would attribute someone else's change to this user. */
        return { ok: false, reason: 'a new rule appeared, but not the one you entered - somebody else may be editing device scope at the same time. Worth looking at the list before continuing.' };
      }

      const preexisting = rules.find(sameRule);
      if (preexisting) {
        return { ok: false, reason: 'that rule already existed before you clicked, and no new rule was added - so the save did not take. The page should be showing why just under the form.' };
      }

      return { ok: false, reason: 'the rule is not there when read back from the server. Whatever the button did, nothing was saved - the message under the form is the application\'s own explanation.' };
    },
  };

  return api;
}

export const DEVICE_SCOPE_SELECTORS = SEL;
